if (!customElements.get('product-gallery')) {
  class ProductGallery extends window.BaseComponent {
    constructor() {
      super();
    }

    init() {
      this.sectionId = this.dataset.section;
      this.initSwiper();
      this.initPagination();
      this.initThumbnails();
      this.initGallerySwipe();
      this.initZoom();

      // Listen for variant changes to slide gallery
      if (window.pubsub) {
        window.pubsub.subscribe('variant-change', (variant) => {
          if (variant && variant.featured_media) {
            this.slideToMedia(variant.featured_media.id);
          }
        });
      }
    }

    initSwiper() {
      if (typeof Swiper === 'undefined') {
        console.error('Swiper not found. Media gallery requires Swiper bundle.');
        return;
      }

      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) {
        console.warn('ProductGallery: No .swiper container found.');
        return;
      }

      this.swiper = new Swiper(swiperEl, {
        // Mobile: auto width (respects CSS 88%) + centered so first image
        // appears centered with equal space on both sides (SFTM-style peek).
        slidesPerView: 'auto',
        centeredSlides: true,
        autoHeight: true,
        breakpoints: {
          // Desktop: restore single full-width slide, no centering offset
          750: {
            slidesPerView: 1,
            centeredSlides: false
          }
        },
        on: {
          slideChange: () => {
            this.updatePagination(this.swiper.activeIndex);
            this.updateThumbnails(this.swiper.activeIndex);
            this.refreshLenis();
          }
        }
      });
    }

    initPagination() {
      this.pagination = document.querySelector(`.dot-pagination[data-pagination-for="${this.sectionId}"]`);
      if (!this.pagination) return;

      this.dots = this.pagination.querySelectorAll('.dot-pagination__dot');
      this.prevBtn = this.pagination.querySelector('.dot-pagination__arrow--prev');
      this.nextBtn = this.pagination.querySelector('.dot-pagination__arrow--next');

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index);
          this.swiper.slideTo(index);
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.swiper.slidePrev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.swiper.slideNext());
      }
    }

    initThumbnails() {
      this.thumbnailsWrapper = document.querySelector('.product__thumbnails');
      if (!this.thumbnailsWrapper) return;

      this.thumbs = this.thumbnailsWrapper.querySelectorAll('.product__thumbnail-item');
      this.thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.dataset.index);
          this.swiper.slideTo(index);
        });
      });
    }

    /**
     * Trackpad horizontal-swipe support directly on the product gallery.
     * Hovering the product image and swiping left/right on a trackpad
     * (wheel event with deltaX) advances/retreats the Swiper gallery,
     * mirroring the same gesture available in the zoom lightbox.
     * Desktop only — mobile relies on native touch swipe across slides.
     */
    initGallerySwipe() {
      if (!this.swiper) return;
      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) return;

      this.gallerySwipeMql = window.matchMedia('(min-width: 750px)');

      swiperEl.addEventListener('wheel', (event) => {
        if (!this.gallerySwipeMql.matches) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 12) {
          event.preventDefault();
          if (this._gallerySwipeLock) return;
          this._gallerySwipeLock = true;
          if (event.deltaX > 0) {
            this.swiper.slideNext();
          } else {
            this.swiper.slidePrev();
          }
          setTimeout(() => { this._gallerySwipeLock = false; }, 350);
        }
      }, { passive: false });
    }

    /**
     * Click-to-zoom lightbox (desktop only) with gallery navigation.
     * - Clicking a product image opens it large in a fixed overlay modal.
     * - Prev/Next arrow buttons step through the product's other photos.
     * - Left/Right arrow keys, and horizontal trackpad swipe (wheel deltaX),
     *   also step through photos while the modal is open.
     * - Disabled on mobile (max-width: 749px), where pinch-to-zoom on the
     *   gallery itself is used instead.
     * High-res URLs live in `data-zoom-src` on each `.product__media-item`
     * wrapper (set in snippets/product-media.liquid).
     */
    initZoom() {
      this.zoomModal = document.querySelector('[data-zoom-modal]');
      if (!this.zoomModal) return;

      this.zoomImage = this.zoomModal.querySelector('[data-zoom-image]');
      this.zoomInner = this.zoomModal.querySelector('[data-zoom-inner]');
      this.zoomCloseBtn = this.zoomModal.querySelector('[data-zoom-close]');
      this.zoomPrevBtn = this.zoomModal.querySelector('[data-zoom-prev]');
      this.zoomNextBtn = this.zoomModal.querySelector('[data-zoom-next]');
      this.zoomMql = window.matchMedia('(min-width: 750px)');
      this.zoomIndex = 0;

      // Build the navigable list of zoomable items (images only) in gallery order
      this.zoomWrappers = [...this.querySelectorAll('.product__media-item[data-zoom-src]')];

      this.zoomableImages = this.querySelectorAll('.product__media-item img.product__media');
      this.zoomableImages.forEach((img) => {
        img.addEventListener('click', (event) => {
          if (!this.zoomMql.matches) return;
          event.preventDefault();
          this.openZoom(img);
        });
      });

      if (this.zoomCloseBtn) {
        this.zoomCloseBtn.addEventListener('click', () => this.closeZoom());
      }

      if (this.zoomPrevBtn) {
        this.zoomPrevBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.zoomStep(-1);
        });
      }

      if (this.zoomNextBtn) {
        this.zoomNextBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.zoomStep(1);
        });
      }

      this.zoomModal.addEventListener('click', (event) => {
        if (event.target === this.zoomModal || event.target === this.zoomInner) {
          this.closeZoom();
        }
      });

      // Horizontal trackpad swipe support while zoomed in
      this.zoomModal.addEventListener('wheel', (event) => {
        if (!this.zoomModal.classList.contains('is-active')) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 12) {
          event.preventDefault();
          if (this._zoomWheelLock) return;
          this._zoomWheelLock = true;
          this.zoomStep(event.deltaX > 0 ? 1 : -1);
          setTimeout(() => { this._zoomWheelLock = false; }, 350);
        }
      }, { passive: false });

      if (!window.__productZoomKeysBound) {
        document.addEventListener('keydown', (event) => {
          const modal = document.querySelector('[data-zoom-modal].is-active');
          if (!modal) return;

          if (event.key === 'Escape') {
            modal.classList.remove('is-active');
            modal.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('product-zoom-open');
            const img = modal.querySelector('[data-zoom-image]');
            if (img) img.src = '';
            return;
          }

          if (event.key === 'ArrowRight') {
            window.dispatchEvent(new CustomEvent('product-zoom:step', { detail: { direction: 1 } }));
          } else if (event.key === 'ArrowLeft') {
            window.dispatchEvent(new CustomEvent('product-zoom:step', { detail: { direction: -1 } }));
          }
        });
        window.__productZoomKeysBound = true;
      }

      window.addEventListener('product-zoom:step', (event) => {
        if (!this.zoomModal.classList.contains('is-active')) return;
        this.zoomStep(event.detail.direction);
      });
    }

    openZoom(img) {
      if (!this.zoomModal || !this.zoomImage) return;
      const wrapper = img.closest('.product__media-item');
      const idx = this.zoomWrappers.indexOf(wrapper);
      this.renderZoom(idx === -1 ? 0 : idx);
      this.zoomModal.classList.add('is-active');
      this.zoomModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-zoom-open');
    }

    renderZoom(index) {
      if (!this.zoomWrappers.length) return;
      const total = this.zoomWrappers.length;
      this.zoomIndex = ((index % total) + total) % total;

      const wrapper = this.zoomWrappers[this.zoomIndex];
      const sourceImg = wrapper.querySelector('img');

      this.zoomImage.src = wrapper.dataset.zoomSrc;
      this.zoomImage.alt = sourceImg ? (sourceImg.alt || '') : '';

      const multiple = total > 1;
      if (this.zoomPrevBtn) this.zoomPrevBtn.hidden = !multiple;
      if (this.zoomNextBtn) this.zoomNextBtn.hidden = !multiple;
    }

    zoomStep(direction) {
      this.renderZoom(this.zoomIndex + direction);
    }

    closeZoom() {
      if (!this.zoomModal) return;
      this.zoomModal.classList.remove('is-active');
      this.zoomModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-zoom-open');
      if (this.zoomImage) this.zoomImage.src = '';
    }

    updatePagination(index) {
      if (!this.dots) return;
      this.dots.forEach(dot => {
        dot.classList.toggle('is-active', parseInt(dot.dataset.index) === index);
      });
    }

    updateThumbnails(index) {
      if (!this.thumbs) return;
      this.thumbs.forEach(thumb => {
        thumb.classList.toggle('is-active', parseInt(thumb.dataset.index) === index);
      });
    }

    slideToMedia(mediaId) {
      const targetSlide = this.querySelector(`.swiper-slide[data-media-id="${mediaId}"]`);
      if (targetSlide) {
        const index = [...this.querySelectorAll('.swiper-slide')].indexOf(targetSlide);
        if (index !== -1) {
          this.swiper.slideTo(index);
          this.refreshLenis();
        }
      }
    }

    refreshLenis() {
      if (this._lenisTimer) clearTimeout(this._lenisTimer);
      this._lenisTimer = setTimeout(() => {
        if (window.lenis) window.lenis.resize();
      }, 350);
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy();
      }
    }
  }

  if (window.BaseComponent) {
    customElements.define('product-gallery', ProductGallery);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (window.BaseComponent) customElements.define('product-gallery', ProductGallery);
    });
  }
}
